import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface CategoryObject {
  id: string;
  nameEn: string;
  nameAe: string;
}

export interface CategoryListInfo {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  category: Array<CategoryObject>;
  errors: unknown;
}

export interface CategoryFilterInfo {
  name: string;
}

@Module
export default class CategoryModule
  extends VuexModule
  implements CategoryListInfo
{
  category = [] as Array<CategoryObject>;
  allCategory = [] as Array<CategoryObject>;
  categoryFilter = {} as CategoryFilterInfo;
  page = 1;
  take = 10;
  itemCount = 0;
  pageCount = 1;
  hasPreviousPage = false;
  hasNextPage = true;
  errors = {};

  /**
   * Get category list
   * @returns Array<CategoryObject>
   */
  get categoryList(): Array<CategoryObject> {
    return this.category;
  }

  get allCategoryList(): Array<CategoryObject> {
    return this.allCategory;
  }

  /**
   * Get total items
   * @returns number
   */
  get categoryListCount(): number {
    return this.itemCount;
  }

  @Mutation
  [Mutations.SET_CATEGORIES](data) {
    this.category = data.data;
    this.page = data.meta.page;
    this.take = data.meta.take;
    this.itemCount = data.meta.itemCount;
    this.pageCount = data.meta.pageCount;
    this.hasPreviousPage = data.meta.hasPreviousPage;
    this.hasNextPage = data.meta.hasNextPage;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_CATEGORIES](data) {
    this.allCategory = data.data;
  }

  @Mutation
  [Mutations.SET_CATEGORY_CURRENT_PAGE](page) {
    this.page = page;
  }

  @Mutation
  [Mutations.RESET_CATEGORY_LIST]() {
    this.category = [] as Array<CategoryObject>;
    this.page = 1;
    this.take = 10;
    this.itemCount = 0;
    this.pageCount = 1;
    this.hasPreviousPage = false;
    this.hasNextPage = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_CATEGORY_FILTER](obj) {
    this.categoryFilter.name = obj.name;
  }

  @Action
  [Actions.RESET_CATEGORY_STORE]() {
    this.context.commit(Mutations.RESET_CATEGORY_LIST);
  }

  @Action
  [Actions.UPDATE_CATEGORY_FILTER](val) {
    this.context.commit(Mutations.SET_CATEGORY_FILTER, val);
  }

  @Action
  [Actions.CREATE_CATEGORY](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/category', data)
      .then((data) => {
        this.context.commit(Mutations.RESET_CATEGORY_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.DELETE_CATEGORY](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/category', id)
      .then(() => {
        this.context.commit(Mutations.RESET_CATEGORY_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_CATEGORY]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/category', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_CATEGORY_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.UPDATE_CATEGORY_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_CATEGORY_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_CATEGORY](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/category', id);
  }

  @Action
  [Actions.UPLOAD_CATEGORY_ATTACHMENT]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/category/attachments/${id}`, data)
      .then(() => {
        this.context.commit(Mutations.RESET_GROOMERS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.statusText],
        });
      });
  }

  @Action
  [Actions.GET_CATEGORIES]() {
    const params = {
      page: this.page,
      name: this.categoryFilter.name,
    };
    ApiService.setAuthorizationHeader();
    return ApiService.query('/category', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_CATEGORIES, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_CATEGORY]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get(`/category/all`)
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_CATEGORIES, data);
        return data.data;
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
